import React, { JSX } from 'react';
import { Field, ErrorMessage, FormikErrors, FormikTouched } from 'formik';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import { SellerFormValueType as FormValueType } from '@interfaces/models/seller-info/seller-information';
import { SellerInformationContext } from '@context/seller-information.context';
import { useContextSelector } from 'use-context-selector';
import styles from '../seller-information-form/seller-information-form.module.scss';
import SellerFormTextInput from './seller-form-text-input';

type AddressInformationProps = {
  values: FormValueType;
  errors?: FormikErrors<FormValueType>;
  touched?: FormikTouched<FormValueType>;
  ErrorLabel: (message: string) => JSX.Element;
  dirty?: boolean;
};

const AddressInformation: React.FC<AddressInformationProps> = (props): JSX.Element => {
  const { values, errors, touched, ErrorLabel, dirty } = props;
  const currentStates = useContextSelector(SellerInformationContext, (value) => value.currentStates);
  const stateIsRequired = useContextSelector(SellerInformationContext, (value) => value.stateIsRequired);
  const { t } = useTranslation('get-paid');
  return (
    <div className={clsx(styles.formGroup, styles['formGroup--formGroupFlexContainer'])}>
      {/* Street */}
      <div
        className={clsx('vc-formUnit', styles['vc-formUnit'], {
          [styles.error + ' error']: errors?.street && touched?.street,
        })}
      >
        <SellerFormTextInput
          name="street"
          label={t('GET_PAID.PERSONAL_INFORMATION_FORM.LABEL.STREET')}
          value={values.street}
          placeholder={t('GET_PAID.PERSONAL_INFORMATION_FORM.PLACEHOLDER.STREET')}
          required
          maxLength={100}
          t={t}
          dirty={dirty}
          touched={touched}
        />
      </div>
      {/* City */}
      <div
        className={clsx('vc-formUnit', styles['vc-formUnit'], {
          [styles.error + ' error']: errors?.city && touched?.city,
        })}
      >
        <SellerFormTextInput
          name="city"
          label={t('GET_PAID.PERSONAL_INFORMATION_FORM.LABEL.CITY')}
          value={values.city}
          placeholder={t('GET_PAID.PERSONAL_INFORMATION_FORM.PLACEHOLDER.CITY')}
          required
          maxLength={100}
          t={t}
          dirty={dirty}
          touched={touched}
        />
      </div>
      {/* Zip */}
      <div
        className={clsx('vc-formUnit', styles['vc-formUnit'], {
          [styles.error + ' error']: errors?.zip && touched?.zip,
        })}
      >
        <SellerFormTextInput
          name="zip"
          label={t('GET_PAID.PERSONAL_INFORMATION_FORM.LABEL.ZIPCODE')}
          value={values.zip}
          placeholder={t('GET_PAID.PERSONAL_INFORMATION_FORM.PLACEHOLDER.ZIPCODE')}
          required
          maxLength={100}
          t={t}
          dirty={dirty}
          touched={touched}
        />
      </div>
      {/* State */}
      {stateIsRequired && (
        <>
          {!currentStates.length ? (
            <div
              className={clsx('vc-formUnit', styles['vc-formUnit'], {
                [styles.error + ' error']: errors?.state && touched?.state,
              })}
            >
              <SellerFormTextInput
                name="state"
                label={t('GET_PAID.PERSONAL_INFORMATION_FORM.LABEL.STATE')}
                value={values.state}
                placeholder={t('GET_PAID.PERSONAL_INFORMATION_FORM.PLACEHOLDER.STATE')}
                required
                maxLength={100}
                t={t}
                dirty={dirty}
                touched={touched}
              />
            </div>
          ) : (
            <div
              className={clsx(
                'vc-formUnit',
                'vc-formUnit--select',
                styles['vc-formUnit'],
                styles['vc-formUnit--select'],
                {
                  [styles.error + ' error']: errors?.state && touched?.state,
                },
              )}
            >
              <Field
                as="select"
                name="state"
                aria-labelledby="label-state"
                className={clsx('vc-inputText', styles['vc-inputText'], {
                  [styles['vc-inputText--touched']]: dirty && touched?.country,
                })}
                value={values.state}
                required
              >
                <option
                  value=""
                  disabled
                >
                  {t('GET_PAID.PERSONAL_INFORMATION_FORM.PLACEHOLDER.STATE')}
                </option>
                {currentStates.map((state) => (
                  <option
                    key={state.iso}
                    value={state.iso}
                  >
                    {state.name}
                  </option>
                ))}
              </Field>
              <label
                id="label-state"
                className={clsx('vc-inputText-label', styles['vc-inputText-label'], styles.required)}
              >
                {t('GET_PAID.PERSONAL_INFORMATION_FORM.LABEL.STATE')}
              </label>
              <ErrorMessage
                name="state"
                render={ErrorLabel}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AddressInformation;
